import React from 'react';
import { Link } from 'react-router-dom';
import './landing.css';
import useLegalPage from './useLegalPage';
import openExternal from './openExternal';
import LogoMark from './art/LogoMark';

// Generated from the LegalApp design canvas. Class names carry the "pp-" prefix
// so they never collide with the rest of the app's CSS.
export default function PrivacyPage() {
  const { rootRef, navTo } = useLegalPage('PrivacyPage');
  return (
    <div className="pp-pp" ref={rootRef}>
      <header className="pp-nav">
        <div className="pp-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", minHeight: "76px" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }} aria-label="LegalApp home">
            <LogoMark />
            <span className="pp-d" style={{ fontSize: "24px", fontWeight: "700", letterSpacing: "-0.01em", color: "#0B1B3F" }}>
              {"Legal"}
              <span style={{ color: "#1D5BD8" }}>
                {"App"}
              </span>
            </span>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
            <Link to="/" className="pp-back" aria-label="Back to home">
              <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }} aria-hidden="true">
                <path className="pp-ic" d="M19 12H5M11 6l-6 6 6 6" />
              </svg>
              <span>
                {"Back"}
                <span className="pp-hide-sm">
                  {" to home"}
                </span>
              </span>
            </Link>
            <a className="pp-btn" href="https://wa.me/919582822225?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20of%20LegalApp." target="_blank" rel="noopener noreferrer" onClick={openExternal} title="Book a demo on WhatsApp">
              {"Book a demo"}
            </a>
          </div>
        </div>
      </header>
      <section style={{ background: "#F8FAFE", borderBottom: "1px solid #EDF1F8", padding: "clamp(48px, 7vw, 88px) 0 clamp(40px, 5vw, 64px)" }}>
        <div className="pp-wrap" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div className="pp-eyebrow" style={{ color: "#1D5BD8" }}>
            {"Legal"}
          </div>
          <h1 className="pp-d" style={{ fontSize: "clamp(44px, 6vw, 72px)", lineHeight: "1", fontWeight: "700", letterSpacing: "-0.02em", color: "#0B1B3F" }}>
            {"Privacy Policy"}
          </h1>
          <p style={{ fontSize: "19px", color: "#5A6684" }}>
            {"LexOnAlgo Advisory Private Limited"}
          </p>
        </div>
      </section>
      <section style={{ padding: "clamp(48px, 6vw, 80px) 0 clamp(64px, 8vw, 104px)" }}>
        <div className="pp-wrap pp-layout">
          <nav className="pp-toc" aria-label="On this page">
            <div className="pp-eyebrow" style={{ fontSize: "12px", color: "#5A6684", padding: "0 8px 8px" }}>
              {"On this page"}
            </div>
            <a href="#s1" onClick={navTo}>
              <span className="pp-m">
                {"1"}
              </span>
              <span>
                {"Introduction"}
              </span>
            </a>
            <a href="#s2" onClick={navTo}>
              <span className="pp-m">
                {"2"}
              </span>
              <span>
                {"Information We Collect"}
              </span>
            </a>
            <a href="#s3" onClick={navTo}>
              <span className="pp-m">
                {"3"}
              </span>
              <span>
                {"How We Use Your Information"}
              </span>
            </a>
            <a href="#s4" onClick={navTo}>
              <span className="pp-m">
                {"4"}
              </span>
              <span>
                {"Data Sharing and Disclosure"}
              </span>
            </a>
            <a href="#s5" onClick={navTo}>
              <span className="pp-m">
                {"5"}
              </span>
              <span>
                {"Data Security"}
              </span>
            </a>
            <a href="#s6" onClick={navTo}>
              <span className="pp-m">
                {"6"}
              </span>
              <span>
                {"Data Retention"}
              </span>
            </a>
            <a href="#s7" onClick={navTo}>
              <span className="pp-m">
                {"7"}
              </span>
              <span>
                {"Your Rights"}
              </span>
            </a>
            <a href="#s8" onClick={navTo}>
              <span className="pp-m">
                {"8"}
              </span>
              <span>
                {"Changes to This Privacy Policy"}
              </span>
            </a>
            <a href="#s9" onClick={navTo}>
              <span className="pp-m">
                {"9"}
              </span>
              <span>
                {"Contact Us"}
              </span>
            </a>
          </nav>
          <article className="pp-article">
            <div id="s1" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"01"}
                </span>
                <span>
                  {"Introduction"}
                </span>
              </h2>
              <p>
                {"Welcome to LegalApp (\"we,\" \"us,\" \"our\"). LegalApp is an AI assistant for investigating officers. It helps with complaint intake and FIR drafting, investigation planning, identifying and analysing evidence, identifying and questioning witnesses and accused persons, recording statements and drafting the case diary, and preparing notices and chargesheets. Complainants may also use LegalApp's guided complaint interface."}
              </p>
              <p>
                {"We are committed to protecting your privacy and ensuring the security of your personal and case-related data. This Privacy Policy outlines how we collect, use, store, and protect your information when you use our platform."}
              </p>
              <p>
                {"By using our services, you agree to the terms of this Privacy Policy. If you do not agree with any part of this policy, please do not use our platform."}
              </p>
            </div>
            <div id="s2" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"02"}
                </span>
                <span>
                  {"Information We Collect"}
                </span>
              </h2>
              <p>
                {"We collect the following types of information:"}
              </p>
              <h3>
                <span className="pp-m">
                  {"2.1"}
                </span>
                {"Personal Information"}
              </h3>
              <ul>
                <li>
                  {"Name, email address, phone number, and other contact details."}
                </li>
                <li>
                  {"For investigating officers and other police users: official details such as rank or designation, police station, and district."}
                </li>
                <li>
                  {"For counsel: professional details such as bar enrolment number, law firm or chamber, and designation."}
                </li>
                <li>
                  {"For complainants: contact details and the information provided while filing a complaint."}
                </li>
              </ul>
              <h3>
                <span className="pp-m">
                  {"2.2"}
                </span>
                {"Case Data"}
              </h3>
              <ul>
                <li>
                  {"Complaints, FIR drafts, investigation plans, statements of witnesses and accused persons, case diary entries, notices, chargesheet drafts, and other case records created on or uploaded to the platform."}
                </li>
                <li>
                  {"Evidence uploaded for analysis, including documents, photographs, and audio and video recordings."}
                </li>
                <li>
                  {"Metadata associated with uploaded files, such as file names, sizes, and formats."}
                </li>
              </ul>
              <p>
                {"Case data may include personal information about complainants, victims, witnesses, and accused persons. Users provide such information in the course of a lawful investigation or proceeding and are responsible for ensuring that they are authorised to do so."}
              </p>
              <h3>
                <span className="pp-m">
                  {"2.3"}
                </span>
                {"Usage Data"}
              </h3>
              <ul>
                <li>
                  {"Information about how you interact with our platform, including IP addresses, device information, browser type, and usage patterns."}
                </li>
              </ul>
              <h3>
                <span className="pp-m">
                  {"2.4"}
                </span>
                {"Third-Party Data"}
              </h3>
              <ul>
                <li>
                  {"Information obtained from third-party services integrated with our platform, such as cloud storage providers or legal databases."}
                </li>
              </ul>
            </div>
            <div id="s3" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"03"}
                </span>
                <span>
                  {"How We Use Your Information"}
                </span>
              </h2>
              <p>
                {"We use your information for the following purposes:"}
              </p>
              <h3>
                <span className="pp-m">
                  {"3.1"}
                </span>
                {"Investigation Assistance"}
              </h3>
              <p>
                {"To analyse case data and generate AI-assisted outputs, such as FIR drafts, investigation plans, lists of potential evidence and witnesses, analysis of documents, photographs and recordings, examination and interrogation questions, case diary entries, notices, and chargesheet drafts, in the language chosen by the user. These outputs are suggestions for review; the investigating officer remains responsible for every decision and every document filed."}
              </p>
              <h3>
                <span className="pp-m">
                  {"3.2"}
                </span>
                {"Platform Improvement"}
              </h3>
              <p>
                {"To enhance and optimize our platform’s functionality, user experience, and performance."}
              </p>
              <h3>
                <span className="pp-m">
                  {"3.3"}
                </span>
                {"Communication"}
              </h3>
              <p>
                {"To respond to your inquiries and demo requests, provide customer support, and send service-related notifications."}
              </p>
              <h3>
                <span className="pp-m">
                  {"3.4"}
                </span>
                {"Compliance"}
              </h3>
              <p>
                {"To comply with legal obligations, court orders, or regulatory requirements."}
              </p>
            </div>
            <div id="s4" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"04"}
                </span>
                <span>
                  {"Data Sharing and Disclosure"}
                </span>
              </h2>
              <p>
                {"We do not sell or trade your personal or case-related data. We disclose it only in the following limited circumstances:"}
              </p>
              <h3>
                <span className="pp-m">
                  {"4.1"}
                </span>
                {"Service Providers"}
              </h3>
              <p>
                {"To trusted third parties that host, store, or process data on our behalf, such as cloud hosting and storage providers, providers of AI and language-processing services, and the third-party services described in Section 2.4. They may use the data only to provide their services to us, under obligations of confidentiality and security."}
              </p>
              <h3>
                <span className="pp-m">
                  {"4.2"}
                </span>
                {"Legal Requirements"}
              </h3>
              <p>
                {"Where disclosure is required by law, a court order, or a lawful request from a competent authority, as described in Section 3.4."}
              </p>
              <h3>
                <span className="pp-m">
                  {"4.3"}
                </span>
                {"With Your Consent"}
              </h3>
              <p>
                {"Where you ask us to share information, or otherwise consent to it."}
              </p>
            </div>
            <div id="s5" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"05"}
                </span>
                <span>
                  {"Data Security"}
                </span>
              </h2>
              <p>
                {"We implement industry-standard security measures to protect your data, including encryption, access controls, and regular security audits. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security."}
              </p>
            </div>
            <div id="s6" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"06"}
                </span>
                <span>
                  {"Data Retention"}
                </span>
              </h2>
              <p>
                {"We retain your personal and case-related data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law."}
              </p>
            </div>
            <div id="s7" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"07"}
                </span>
                <span>
                  {"Your Rights"}
                </span>
              </h2>
              <p>
                {"You have the following rights regarding your data:"}
              </p>
              <h3>
                <span className="pp-m">
                  {"7.1"}
                </span>
                {"Access and Correction"}
              </h3>
              <p>
                {"You may access, update, or correct your personal information by contacting us."}
              </p>
              <h3>
                <span className="pp-m">
                  {"7.2"}
                </span>
                {"Data Deletion"}
              </h3>
              <p>
                {"If you want to delete your account or data, just reach out to us. Send an email from your registered email address to "}
                <a href="mailto:contact@legalapp.in">
                  {"contact@legalapp.in"}
                </a>
                {", or message us on WhatsApp from your registered mobile number at "}
                <a href="https://wa.me/919582822225" target="_blank" rel="noopener noreferrer" onClick={openExternal}>
                  {"+91 95828 22225"}
                </a>
                {". We’ll verify the request and handle the deletion for you, subject to any records we are required to keep by law."}
              </p>
              <h3>
                <span className="pp-m">
                  {"7.3"}
                </span>
                {"Opt-Out"}
              </h3>
              <p>
                {"You may opt out of receiving promotional communications from us. Send an email from your registered email address to "}
                <a href="mailto:contact@legalapp.in">
                  {"contact@legalapp.in"}
                </a>
                {", or message us on WhatsApp from your registered mobile number at "}
                <a href="https://wa.me/919582822225" target="_blank" rel="noopener noreferrer" onClick={openExternal}>
                  {"+91 95828 22225"}
                </a>
                {". We’ll verify the request and update our records."}
              </p>
            </div>
            <div id="s8" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"08"}
                </span>
                <span>
                  {"Changes to This Privacy Policy"}
                </span>
              </h2>
              <p>
                {"We may update this Privacy Policy from time to time. Any changes will be posted on our platform. Your continued use of our services constitutes acceptance of the revised policy."}
              </p>
            </div>
            <div id="s9" className="pp-sec">
              <h2>
                <span className="pp-m">
                  {"09"}
                </span>
                <span>
                  {"Contact Us"}
                </span>
              </h2>
              <p className="pp-note">
                {"If you have any questions or concerns about this Privacy Policy, please email us at "}
                <a href="mailto:contact@legalapp.in">
                  {"contact@legalapp.in"}
                </a>
                {" or message us on WhatsApp at "}
                <a href="https://wa.me/919582822225" target="_blank" rel="noopener noreferrer" onClick={openExternal}>
                  {"+91 95828 22225"}
                </a>
                {"."}
              </p>
            </div>
          </article>
        </div>
      </section>
      <footer className="pp-foot" style={{ borderTop: "1px solid #DCE4F2", padding: "32px 0" }}>
        <div className="pp-wrap" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px 24px", fontSize: "14px", color: "#5A6684" }}>
          <span>
            {"© 2026 LexOnAlgo Advisory Pvt. Ltd. All rights reserved."}
          </span>
          <span style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
            <Link to="/">
              {"Home"}
            </Link>
            <Link to="/terms">
              {"Terms of Use"}
            </Link>
            <a href="mailto:contact@legalapp.in">
              {"contact@legalapp.in"}
            </a>
            <a href="https://wa.me/919582822225" target="_blank" rel="noopener noreferrer" onClick={openExternal}>
              {"Support"}
            </a>
          </span>
        </div>
      </footer>

    </div>
  );
}
